import {
    type AiAgentId,
    CONVERSATIONAL_MM_AGENT,
} from '@fruitful/llm-service-shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetcher } from '../../http/Fetcher';
import { captureException } from '../../logger/Logger';

/**
 * Signup/conversion stats for a single CMM mastra thread, resolved from the
 * backend `mastra_threads` table (the same source the thread detail page reads
 * one-at-a-time via `/api/admin/mastra-threads/:id`). A non-null `membershipId`
 * / `userId` means the visitor behind the thread converted to a member.
 */
export interface MastraThreadSignupStat {
    mastraThreadId: string;
    membershipId: string | null;
    userId: string | null;
    signedUp: boolean;
    signedUpAt: string | null;
}

/** Keyed by `mastraThreadId`. */
export type MastraThreadStatsResponse = Record<string, MastraThreadSignupStat>;

const STATS_ENDPOINT = '/api/admin/mastra-threads/stats';

// Keep each POST body bounded so a full-dashboard load is a handful of calls
// rather than one per row (avoids N+1) and never one giant request.
const STATS_CHUNK_SIZE = 200;

const chunk = <T>(items: readonly T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }
    return chunks;
};

/**
 * Batch-resolves signup stats for the given CMM thread ids in as few backend
 * calls as possible, so the Threads table can show a linkable Membership Id
 * column without an N+1 admin fetch per row. Only runs for the CMM agent.
 */
export const useMastraThreadStats = ({
    agentId,
    threadIds,
    enabled = true,
}: {
    agentId: AiAgentId;
    threadIds: readonly string[];
    enabled?: boolean;
}) => {
    const isCmm = agentId === CONVERSATIONAL_MM_AGENT.id;

    // Stable, order-independent key so the query is not refetched purely
    // because the incoming array reference changed.
    const sortedIds = useMemo(() => [...threadIds].sort(), [threadIds]);

    return useQuery<MastraThreadStatsResponse>({
        queryKey: ['mastraThreadStats', agentId, sortedIds],
        queryFn: async () => {
            try {
                const responses = await Promise.all(
                    chunk(sortedIds, STATS_CHUNK_SIZE).map(
                        async (mastraThreadIds) =>
                            await fetcher<MastraThreadStatsResponse>({
                                endpoint: STATS_ENDPOINT,
                                method: 'POST',
                                body: JSON.stringify({ mastraThreadIds }),
                            }),
                    ),
                );

                const merged: MastraThreadStatsResponse = {};
                for (const response of responses) {
                    Object.assign(merged, response);
                }
                return merged;
            } catch (err) {
                captureException(err);
                throw err;
            }
        },
        enabled: enabled && isCmm && sortedIds.length > 0,
        refetchOnWindowFocus: false,
    });
};
